import { useState, useEffect, useMemo } from 'react';
import { Star, Truck, ShieldCheck, ArrowRight, Heart, ShoppingCart, Plus, Minus, PackageCheck, MapPin } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import { useWishlist } from '../../context/WishlistContext';
import type { Product, ProductVariant } from '../../data/products';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import SizeFinderDialog from './SizeFinderDialog';

type ProductInfoProps = {
  product: Product;
  onVariantChange?: (variant: ProductVariant) => void;
};

type FeatureSection = {
  heading: string;
  body: string;
};

type EmbeddedSections = {
  intro: string;
  sections: FeatureSection[];
};

const formatMntPrice = (value: number): string =>
  `₮${Math.max(0, Math.round(value)).toLocaleString()}`;

const toNumericSize = (value: string) => {
  const match = value.match(/[\d.]+/);
  return match ? Number.parseFloat(match[0]) : Number.NaN;
};

const splitFeatureEntry = (value: string): FeatureSection | null => {
  const cleaned = value.replace(/\s+/g, ' ').trim();
  if (!cleaned) return null;

  const match = cleaned.match(/^(.{2,70}?)\s+[—-]\s+(.+)$/);
  if (!match) return null;

  const heading = match[1]?.trim();
  const body = match[2]?.trim();
  if (!heading || !body) return null;

  return { heading, body };
};

const cleanDetailBullet = (value: string): string =>
  value.replace(/^[?*•·-]\s*/, '').trim();

const technicalDetailPatterns = [
  /^Product URL:/i,
  /^Source:/i,
  /^FX (date|source):/i,
  /^Эрэмбэ:/i,
  /^Эх сурвалж:/i,
  /^Зураг Cloudflare R2/i,
  /^Зургийн URL/i,
];

const isTechnicalDetail = (value: string): boolean =>
  technicalDetailPatterns.some((pattern) => pattern.test(value));

const isDefaultVariantName = (value: string | undefined): boolean => {
  const normalized = (value ?? '').trim().toLowerCase();
  return ['', 'default', 'үндсэн'].includes(normalized);
};

const normalizeDetailDisplay = (value: string): string => {
  const cleaned = cleanDetailBullet(value);
  const originalPriceMatch = cleaned.match(/^Original price:\s*(.+)$/i);
  if (originalPriceMatch?.[1]) {
    return `Анхны үнэ: ${originalPriceMatch[1].trim()}`;
  }
  return cleaned;
};

const detailDedupeKey = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  const label = normalized.split(':', 1)[0]?.trim() ?? normalized;
  if (['брэнд', 'brand'].includes(label)) return 'brand';
  if (['анхны үнэ', 'original price'].includes(label)) return 'original-price';
  if (['өнгө', 'color', 'colour', 'сонголт', 'барааны сонголт'].includes(label)) return 'variant-option';
  return normalized;
};

const extractSectionsFromShortDescription = (text: string): EmbeddedSections => {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (!normalized) return { intro: '', sections: [] };

  // Captures headings like "Flexibility: High", "Stability: High"
  const headingRegex = /([A-Z][A-Za-z0-9/&' -]{1,38}:\s*[A-Z][A-Za-z0-9/&' -]{1,22})/g;
  const matches = Array.from(normalized.matchAll(headingRegex));

  if (matches.length === 0) {
    return { intro: normalized, sections: [] };
  }

  const sections: FeatureSection[] = [];
  const intro = normalized.slice(0, matches[0].index ?? 0).trim().replace(/[.:-]\s*$/, '').trim();

  for (let i = 0; i < matches.length; i++) {
    const current = matches[i];
    const next = matches[i + 1];
    const heading = (current[1] || '').trim();
    const bodyStart = (current.index ?? 0) + heading.length;
    const bodyEnd = next ? (next.index ?? normalized.length) : normalized.length;
    const body = normalized.slice(bodyStart, bodyEnd).trim().replace(/[.:-]\s*$/, '').trim();

    if (!heading || !body) continue;
    if (/^(shown|style|weight|heel-to-toe drop|drop|not intended)\b/i.test(heading)) continue;

    sections.push({ heading, body });
  }

  return { intro: intro || normalized, sections };
};

export default function ProductInfo({ product, onVariantChange }: ProductInfoProps) {
  const { addItem } = useCart();
  const { language } = useTheme();
  const { toggleWishlist, isInWishlist } = useWishlist();

  // Select first variant by default
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant>(
    product.variants?.[0] || ({} as ProductVariant)
  );
  const [selectedSize, setSelectedSize] = useState(selectedVariant.sizes?.[0] ?? '');
  const [quantity, setQuantity] = useState(1);
  const [isSizeFinderOpen, setIsSizeFinderOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [recommendedSelection, setRecommendedSelection] = useState<{
    size: string;
    label: 'Recommended' | 'Ойр хэмжээ';
  } | null>(null);
  const isWishlisted = isInWishlist(product.id);
  const currentVariantIds = useMemo(
    () => new Set((product.variants ?? []).map((variant) => variant.id)),
    [product.variants]
  );
  const hasValidSelectedVariant =
    Boolean(selectedVariant?.id) && currentVariantIds.has(selectedVariant.id);

  const availableEuSizes = useMemo(
    () =>
      (selectedVariant.sizes ?? [])
        .map((size) => toNumericSize(size))
        .filter((size) => Number.isFinite(size)),
    [selectedVariant]
  );

  const resolveSizeLabel = (euSize: number) => {
    const sizes = selectedVariant.sizes ?? [];
    const match = sizes.find((size) => {
      const numeric = toNumericSize(size);
      return Number.isFinite(numeric) && Math.abs(numeric - euSize) < 0.001;
    });
    return match ?? euSize.toString();
  };

  const formatSizeLabel = (size: string) => {
    const trimmed = size.trim();
    return /eu/i.test(trimmed) ? trimmed : `EU ${trimmed}`;
  };

  // Keep selected variant in sync when route/product changes.
  useEffect(() => {
    const firstVariant = product.variants?.[0] || ({} as ProductVariant);
    if (!selectedVariant?.id || !currentVariantIds.has(selectedVariant.id)) {
      setSelectedVariant(firstVariant);
    }
  }, [product.id, product.variants, selectedVariant?.id, currentVariantIds]);

  // Notify parent only when selected variant belongs to current product.
  useEffect(() => {
    if (onVariantChange && hasValidSelectedVariant) {
      onVariantChange(selectedVariant);
    }
  }, [selectedVariant, onVariantChange, hasValidSelectedVariant]);

  // Update selected size when variant changes
  useEffect(() => {
    setSelectedSize(selectedVariant.sizes?.[0] ?? '');
    setRecommendedSelection(null);
  }, [selectedVariant]);

  useEffect(() => {
    setIsDescriptionExpanded(false);
  }, [product.id]);

  const handleQuantityDecrease = () => {
    if (quantity > 1) setQuantity(quantity - 1);
  };

  const handleQuantityIncrease = () => {
    setQuantity(quantity + 1);
  };

  const handleAddToCart = () => {
    addItem(product, selectedVariant, selectedSize || null, quantity);
  };

  const handleWishlist = () => {
    toggleWishlist(product);
  };

  const shortDescription =
    product.shortDescription?.trim() || product.description || '';
  const benefits = product.benefits ?? [];
  const productDetails = product.productDetails ?? [];
  const embeddedFromShort = useMemo(
    () => extractSectionsFromShortDescription(shortDescription),
    [shortDescription]
  );
  const featureSections = useMemo(() => {
    const fromBenefitsOrDetails = benefits
      .map((benefit) => splitFeatureEntry(benefit))
      .filter((item): item is FeatureSection => Boolean(item));

    if (fromBenefitsOrDetails.length === 0) {
      // Backward compatibility for old records where feature sections were stored in productDetails.
      fromBenefitsOrDetails.push(
        ...productDetails
          .map((detail) => splitFeatureEntry(detail))
          .filter((item): item is FeatureSection => Boolean(item))
      );
    }

    // If old records have feature headings embedded in shortDescription, merge them in front.
    const combined = [...embeddedFromShort.sections, ...fromBenefitsOrDetails];
    const deduped: FeatureSection[] = [];
    const seen = new Set<string>();
    for (const section of combined) {
      const key = section.heading.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(section);
    }

    return deduped;
  }, [benefits, productDetails, embeddedFromShort.sections]);
  const detailItems = useMemo(() => {
    const cleaned = productDetails
      .map(normalizeDetailDisplay)
      .filter((detail) => detail && !isTechnicalDetail(detail));
    const withoutFeatureLike = cleaned.filter((detail) => !splitFeatureEntry(detail));

    const shouldShowVariantName =
      (product.variants?.length ?? 0) > 1 &&
      selectedVariant?.name &&
      !isDefaultVariantName(selectedVariant.name);
    const shouldShowStyle =
      product.brandSlug === 'nike' &&
      selectedVariant?.sku &&
      !selectedVariant.sku.startsWith('OY-KR-');
    const derived = [
      shouldShowVariantName ? `Shown: ${selectedVariant.name}` : '',
      shouldShowStyle ? `Style: ${selectedVariant.sku}` : '',
    ].filter(Boolean);

    const out: string[] = [];
    const seen = new Set<string>();
    for (const item of [...withoutFeatureLike, ...derived]) {
      const key = detailDedupeKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  }, [product.brandSlug, product.variants?.length, productDetails, selectedVariant?.name, selectedVariant?.sku]);
  const displayDescription = embeddedFromShort.intro || shortDescription;
  const rawSubtitle = product.subtitle?.trim() || '';
  const knownSubtitles = [
    "Men's Shoes",
    "Women's Shoes",
    "Kid's Shoes",
    "Kids' Shoes",
    "Boys' Shoes",
    "Girls' Shoes",
    "Baby/Toddler Shoes",
    "Toddler Shoes",
    "Infant Shoes",
    "Unisex Shoes",
  ];
  const fallbackSubtitle =
    rawSubtitle ||
    knownSubtitles.find((entry) =>
      product.name?.toLowerCase().endsWith(entry.toLowerCase())
    ) ||
    '';
  const subtitle = fallbackSubtitle;
  const displayTitle = subtitle &&
    product.name?.toLowerCase().endsWith(subtitle.toLowerCase())
      ? product.name.slice(0, product.name.length - subtitle.length).trim()
      : product.name;
  const shouldClampDescription = displayDescription.trim().length > 220;
  const collapsedDescriptionStyle =
    shouldClampDescription && !isDescriptionExpanded
      ? {
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
        }
      : undefined;
  const hasDetails = featureSections.length > 0 || detailItems.length > 0;
  const modalImage =
    selectedVariant?.imagePath || product.image_path || product.gallery_paths?.[0] || '';
  const modalPrice = selectedVariant?.price ?? product.price;
  const shouldShowVariantSelector = (product.variants?.length ?? 0) > 1;
  const hasDiscount =
    Boolean(selectedVariant.originalPrice) && Number(selectedVariant.originalPrice) > Number(selectedVariant.price ?? product.price);
  const savings = hasDiscount
    ? Number(selectedVariant.originalPrice) - Number(selectedVariant.price ?? product.price)
    : 0;

  return (
    <div className="space-y-4 pb-4">
      {/* Header */}
      <div className="rounded-[18px] border border-[#e6e6e6] bg-white p-5 shadow-sm sm:p-6">
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-[11px] font-black uppercase tracking-[0.2em] text-[#777d86]">
            {product.category}
          </span>
          <div className="flex items-center gap-1 rounded-full bg-[#fff8ea] px-3 py-1 text-sm font-black text-[#f6a800]">
            <Star size={16} fill="currentColor" />
            <span>{product.rating}</span>
            <span className="ml-1 font-semibold text-[#7a7f87]">
              ({product.reviews} сэтгэгдэл)
            </span>
          </div>
        </div>

        <h1 className="mb-2 break-words text-2xl font-black leading-tight text-[#111318] sm:text-3xl lg:text-4xl">
          {displayTitle}
        </h1>
        {subtitle && (
          <p className="mb-2 text-sm font-semibold text-[#747982]">{subtitle}</p>
        )}

        <div className="mt-4 flex flex-wrap items-end gap-x-3 gap-y-2">
          <span className="text-3xl font-black text-[#111318] sm:text-4xl">
            {formatMntPrice(selectedVariant.price ?? product.price)}
          </span>
          {selectedVariant.originalPrice && (
            <span className="text-base font-semibold text-[#9aa0a8] line-through sm:text-lg">
              {formatMntPrice(selectedVariant.originalPrice)}
            </span>
          )}
          {hasDiscount ? (
            <span className="rounded-full bg-[#f6a800] px-3 py-1 text-xs font-black text-[#111318]">
              {language === 'mn' ? 'Хэмнэлт' : 'Save'} {formatMntPrice(savings)}
            </span>
          ) : null}
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <div className="rounded-[14px] border border-[#e9e9e9] bg-[#f7f7f7] p-4">
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#777d86]">
              {language === 'mn' ? 'Хүргэлт' : 'Delivery'}
            </p>
            <p className="mt-2 text-sm font-extrabold text-[#111318]">
              {language === 'mn' ? 'Баталгаат, найдвартай' : 'Reliable and tracked'}
            </p>
          </div>
          <div className="rounded-[14px] border border-[#e9e9e9] bg-[#f7f7f7] p-4">
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#777d86]">
              {language === 'mn' ? 'Сонголт' : 'Selection'}
            </p>
            <p className="mt-2 text-sm font-extrabold text-[#111318]">
              {selectedVariant.name || (language === 'mn' ? 'Сонгосон хувилбар' : 'Selected variant')}
            </p>
          </div>
          <div className="rounded-[14px] border border-[#e9e9e9] bg-[#f7f7f7] p-4">
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#777d86]">
              {language === 'mn' ? 'Нөөц' : 'Stock'}
            </p>
            <p className="mt-2 text-sm font-extrabold text-[#111318]">
              {selectedVariant.stock && selectedVariant.stock > 0
                ? `${selectedVariant.stock} ${language === 'mn' ? 'ш үлдсэн' : 'left'}`
                : language === 'mn'
                  ? 'Бэлэн байгаа'
                  : 'Available'}
            </p>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="rounded-[18px] border border-[#e6e6e6] bg-white p-5 shadow-sm">
        <div className="space-y-2">
        <p
          className="text-sm font-medium leading-relaxed text-[#5f6570] sm:text-base"
          style={collapsedDescriptionStyle}
        >
          {displayDescription}
        </p>
        <div className="flex items-center gap-4">
          {shouldClampDescription && (
            <Button
              variant="link"
              size="sm"
              className="px-0 h-auto"
              onClick={() => setIsDescriptionExpanded((prev) => !prev)}
            >
              {isDescriptionExpanded ? 'Show less' : 'Read more'}
            </Button>
          )}
          <Button
            variant="link"
            size="sm"
            className="px-0 h-auto"
            onClick={() => setIsDetailsOpen(true)}
          >
            View Product Details
          </Button>
        </div>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-5 rounded-[18px] border border-[#e6e6e6] bg-white p-5 shadow-sm sm:space-y-6 sm:p-6">
        {/* Variant Selector (Thumbnail Images) */}
        {shouldShowVariantSelector && product.variants && product.variants.length > 0 && (
          <div role="group" aria-labelledby="variant-label">
            <label id="variant-label" className="mb-3 block text-sm font-black text-[#111318]">
              Color / Style: <span className="font-semibold text-[#747982]">{selectedVariant.name}</span>
            </label>
            <div className="flex gap-3 flex-wrap" data-testid="variant-select">
              {product.variants.map((variant) => (
                <button
                  key={variant.id}
                  data-testid="variant-option"
                  onClick={() => setSelectedVariant(variant)}
                  aria-label={`Select variant ${variant.name}`}
                  aria-pressed={selectedVariant.id === variant.id}
                  className={[
                    'relative h-16 w-16 overflow-hidden rounded-[14px] border-2 bg-[#f7f7f7] transition-all duration-200',
                    selectedVariant.id === variant.id
                      ? 'scale-105 border-[#f6a800] shadow-[0_6px_18px_rgba(246,168,0,0.22)]'
                      : 'border-[#ececec] hover:border-[#d8d8d8]',
                  ].join(' ')}
                >
                  <img
                    src={variant.imagePath}
                    alt={variant.name}
                    className="h-full w-full object-contain p-1"
                  />
                  {!variant.isAvailable && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-[8px] text-white font-bold">Out</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
            {selectedVariant.stock !== undefined && (
              <p className="mt-2 text-xs font-semibold text-[#858b94]">
                {selectedVariant.stock > 0
                  ? `${selectedVariant.stock} in stock`
                  : 'Out of stock'}
              </p>
            )}
          </div>
        )}

        {/* Sizes (from selected variant) */}
        {selectedVariant.sizes && selectedVariant.sizes.length > 0 && (
          <div role="group" aria-labelledby="size-label">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label id="size-label" className="text-sm font-black text-[#111318]">Хэмжээ</label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsSizeFinderOpen(true)}
                className="text-xs font-semibold"
              >
                📏 Миний хэмжээг ол
              </Button>
            </div>
            {recommendedSelection && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full border border-[var(--ubuy-accent)] bg-amber-50 px-3 py-1 text-xs font-semibold text-[var(--ubuy-title)]">
                  {formatSizeLabel(recommendedSelection.size)} ✓ {recommendedSelection.label}
                </span>
                {selectedSize && selectedSize !== recommendedSelection.size && (
                  <span className="text-xs text-[var(--ubuy-soft-muted)]">
                    Selected: {formatSizeLabel(selectedSize)}
                  </span>
                )}
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {selectedVariant.sizes.map((size) => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  aria-pressed={selectedSize === size}
                  className={[
                    'rounded-[12px] border px-5 py-2.5 text-sm font-black transition-all duration-200',
                    selectedSize === size
                      ? 'border-[#f6a800] bg-[#f6a800] text-[#111318] shadow-sm'
                      : 'border-[#e6e6e6] bg-white text-[#111318] hover:border-[#d8d8d8]',
                  ].join(' ')}
                >
                  {size}
                </button>
              ))}
            </div>

            <SizeFinderDialog
              open={isSizeFinderOpen}
              onOpenChange={setIsSizeFinderOpen}
              availableEuSizes={availableEuSizes}
              onSelectSize={(euSize, source) => {
                const resolved = resolveSizeLabel(euSize);
                setSelectedSize(resolved);
                setRecommendedSelection({
                  size: resolved,
                  label: source === 'recommended' ? 'Recommended' : 'Ойр хэмжээ',
                });
              }}
            />
          </div>
        )}
      </div>

      {/* Quantity & Actions */}
      <div className="space-y-4 rounded-[18px] border border-[#e6e6e6] bg-white p-5 shadow-sm sm:p-6">
        {/* Quantity Selector */}
        <div>
          <label className="mb-3 block text-sm font-black text-[#111318]">Тоо ширхэг</label>
          <div className="inline-flex items-center overflow-hidden rounded-[12px] border border-[#e6e6e6]">
            <button
              onClick={handleQuantityDecrease}
              disabled={quantity <= 1}
              className="px-4 py-3 transition-colors hover:bg-[#f7f7f7] disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Decrease quantity"
            >
              <Minus size={18} className="text-[#111318]" />
            </button>
            <div className="min-w-[60px] border-x border-[#e6e6e6] px-6 py-3 text-center font-black text-[#111318]">
              {quantity}
            </div>
            <button
              onClick={handleQuantityIncrease}
              className="px-4 py-3 transition-colors hover:bg-[#f7f7f7]"
              aria-label="Increase quantity"
            >
              <Plus size={18} className="text-[#111318]" />
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="hidden gap-3 sm:grid sm:grid-cols-[1.8fr,1fr]">
          {/* Add to Cart Button */}
          <button
            id="add-to-cart-button"
            data-testid="add-to-cart-btn"
            onClick={handleAddToCart}
            className="group flex h-14 items-center justify-center gap-2 rounded-[12px] bg-[#f6a800] px-8 font-black text-[#111318] transition-all hover:bg-[#e99f00] active:scale-[0.98]"
          >
            <ShoppingCart size={20} className="transition-transform group-hover:scale-110" />
            <span>ADD TO CART</span>
          </button>

          {/* Wishlist Button */}
          <button
            onClick={handleWishlist}
            className={`group flex h-14 items-center justify-center gap-2 rounded-[12px] border px-6 font-black transition-all active:scale-[0.98] ${
              isWishlisted
                ? 'border-red-400 bg-red-50 text-red-500 hover:bg-red-100'
                : 'border-[#e6e6e6] bg-white text-[#111318] hover:border-[#f6a800] hover:text-[#b87900]'
            }`}
            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart
              size={20}
              fill={isWishlisted ? 'currentColor' : 'none'}
              className="group-hover:scale-110 transition-transform"
            />
            <span>{isWishlisted ? 'SAVED' : 'WISHLIST'}</span>
          </button>
        </div>
      </div>

      {/* Mobile Sticky Actions */}
      <div className="sticky bottom-[max(0.5rem,env(safe-area-inset-bottom))] z-30 rounded-[14px] border border-[#e6e6e6] bg-white p-2 shadow-2xl sm:hidden">
        <div className="grid grid-cols-[1fr,56px] gap-2">
          <button
            data-testid="add-to-cart-btn"
            onClick={handleAddToCart}
            className="group flex h-12 items-center justify-center gap-2 rounded-[12px] bg-[#f6a800] font-black text-[#111318] transition-all hover:bg-[#e99f00] active:scale-[0.98]"
          >
            <ShoppingCart size={18} className="transition-transform group-hover:scale-110" />
            <span>ADD TO CART</span>
          </button>
          <button
            onClick={handleWishlist}
            className={`group flex h-12 items-center justify-center rounded-xl border-2 transition-all active:scale-[0.98] ${
              isWishlisted
                ? 'border-red-400 bg-red-50 text-red-500 hover:bg-red-100'
                : 'border-[var(--ubuy-border-soft)] text-[var(--ubuy-title)] hover:border-[var(--ubuy-accent)] hover:text-[var(--ubuy-accent)]'
            }`}
            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart
              size={20}
              fill={isWishlisted ? 'currentColor' : 'none'}
              className="group-hover:scale-110 transition-transform"
            />
          </button>
        </div>
      </div>

      {/* Features List */}
      {(product.features || []).length > 0 ? (
        <div className="space-y-3 rounded-[15px] border border-[var(--ubuy-border-soft)] bg-white p-5">
          {(product.features || []).map((feature, idx) => (
            <div key={idx} className="flex items-center gap-3 text-sm text-[var(--ubuy-muted)]">
              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[rgba(246,168,0,0.12)] text-[var(--ubuy-accent)]">
                <ArrowRight size={12} />
              </div>
              <span>{feature}</span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="grid gap-3 rounded-[18px] border border-[#e6e6e6] bg-white p-5 text-[11px] font-black uppercase tracking-wider text-[#747982] shadow-sm sm:grid-cols-3">
        <div className="flex items-center gap-2">
          <Truck size={16} className="text-[#f6a800]" />
          <span>Баталгаат хүргэлт</span>
        </div>
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-[#18a970]" />
          <span>Cargo салбар сонголт</span>
        </div>
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-[#2f7ed8]" />
          <span>Чанарын баталгаа</span>
        </div>
      </div>

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="w-[min(96vw,1280px)] max-w-[1280px] max-h-[96vh] overflow-y-auto rounded-[15px] border border-[var(--ubuy-border-soft)] bg-white p-5 sm:p-7 md:p-8">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[var(--ubuy-title)]">
              Product Details
            </DialogTitle>
            <DialogDescription className="sr-only">
              Product details and benefits
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-[96px,1fr] items-center">
            {modalImage ? (
              <img
                src={modalImage}
                alt={product.name}
                className="w-24 h-24 rounded-xl object-cover border border-border/70"
              />
            ) : (
              <div className="w-24 h-24 rounded-xl bg-[var(--ubuy-subtle-surface)] flex items-center justify-center text-[var(--ubuy-soft-muted)] text-xs">
                No image
              </div>
            )}
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider text-[var(--ubuy-muted)]">
                {product.category}
              </p>
              <p className="text-[2rem] sm:text-[2.15rem] font-bold text-[var(--ubuy-title)] leading-tight">
                {displayTitle}
              </p>
              <p className="text-3xl sm:text-[2.2rem] font-bold text-[var(--ubuy-title)] leading-tight">
                {formatMntPrice(modalPrice ?? product.price)}
              </p>
            </div>
          </div>

          {displayDescription && (
            <p className="text-[1rem] leading-7 text-[var(--ubuy-text)]">
              {displayDescription}
            </p>
          )}

          {!hasDetails && (
            <p className="text-sm text-[var(--ubuy-soft-muted)]">
              No additional details available.
            </p>
          )}

          {hasDetails && (
            <div className="space-y-5">
              {featureSections.length > 0 && (
                <div className="space-y-5">
                  {featureSections.map((section, idx) => (
                    <section key={`${section.heading}-${idx}`} className="space-y-1">
                      <h3 className="text-[1.9rem] sm:text-[2rem] leading-tight font-bold text-[var(--ubuy-title)]">
                        {section.heading}
                      </h3>
                      <p className="text-[1rem] leading-7 text-[var(--ubuy-text)]">
                        {section.body}
                      </p>
                    </section>
                  ))}
                </div>
              )}

              {detailItems.length > 0 && (
                <div>
                  <p className="mb-3 text-[1.9rem] sm:text-[2rem] leading-tight font-bold text-[var(--ubuy-title)]">
                    Product Details
                  </p>
                  <ul className="list-disc pl-6 space-y-1.5">
                    {detailItems.map((detail, idx) => (
                      <li key={idx} className="text-[1rem] leading-7 text-[var(--ubuy-text)]">
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}





